const PRINT_ROOT_SELECTOR = '[data-print-options-root]';
const PRINT_ROW_SELECTOR = '[data-print-row]';
const PRINT_SELECT_SELECTOR = '[data-print-variant-select]';
const NO_PRINT_VALUE = '__no_print__';
let printRowIdCounter = 0;

/** Fallback copy, used when a theme setting is left empty. */
const DEFAULT_LABELS = {
  title: 'Egyedi nyomat',
  requiredDescription: 'Legalább 1 nyomat kiválasztása kötelező.',
  optionalDescription: 'Nyomat választása opcionális.',
  selectLabel: 'Nyomat',
  placeholder: 'Válassz nyomatot',
  selectAriaLabel: 'Nyomat kiválasztása',
  add: '+ Nyomat hozzáadása',
  remove: 'Törlés',
  error: 'Legalább egy nyomat kiválasztása kötelező.',
};

function resolveLabels(labels) {
  const resolved = { ...DEFAULT_LABELS };
  if (!labels || typeof labels !== 'object') return resolved;

  for (const [key, value] of Object.entries(labels)) {
    if (typeof value === 'string' && value.trim()) {
      resolved[key] = value;
    }
  }

  return resolved;
}

function createOption(variant) {
  const option = document.createElement('option');
  option.value = String(variant.id);
  option.textContent = variant.title;
  option.disabled = !variant.available;

  return option;
}

function syncDropdownButtonValue(select, valueNode, placeholderText) {
  if (!(select instanceof HTMLSelectElement) || !(valueNode instanceof HTMLElement)) return;

  if (!select.value || select.value === NO_PRINT_VALUE) {
    valueNode.textContent = placeholderText;
    return;
  }

  const selectedOption = select.selectedOptions?.[0];
  valueNode.textContent = selectedOption?.textContent?.trim() || placeholderText;
}

function createPrintRow(variants, labels, selectedValue = '', required = true) {
  const row = document.createElement('div');
  row.className = required
    ? 'print-options__row gpo-element gpo-form__group gpo-col-12'
    : 'print-options__row';
  row.dataset.printRow = '';
  printRowIdCounter += 1;
  const selectId = `print-options-select-${printRowIdCounter}`;

  const selectField = document.createElement('div');
  selectField.className = 'print-options__field';

  const selectLabel = document.createElement('label');
  selectLabel.className = required ? 'gpo-label required print-options__label' : 'print-options__label';
  selectLabel.setAttribute('for', selectId);
  const labelContent = document.createElement('span');
  labelContent.className = 'label-content';
  labelContent.textContent = labels.selectLabel;
  selectLabel.appendChild(labelContent);

  const dropdown = document.createElement('div');
  dropdown.className = required ? 'gpo-dropdown print-options__dropdown gpo-oos--show' : 'print-options__dropdown';

  const dropdownButton = document.createElement('div');
  dropdownButton.className = 'dropdown-button print-options__dropdown-button';

  const dropdownValue = document.createElement('div');
  dropdownValue.className = 'dropdown-button__value';
  dropdownValue.textContent = labels.placeholder;

  const dropdownIcon = document.createElement('div');
  dropdownIcon.className = 'dropdown-button__icon';

  dropdownButton.appendChild(dropdownValue);
  dropdownButton.appendChild(dropdownIcon);

  const select = document.createElement('select');
  select.className = 'print-options__select print-options__native-select';
  select.dataset.printVariantSelect = '';
  select.id = selectId;
  select.setAttribute('aria-label', labels.selectAriaLabel);
  select.required = required;

  const placeholder = document.createElement('option');
  placeholder.value = required ? '' : NO_PRINT_VALUE;
  placeholder.textContent = labels.placeholder;
  placeholder.disabled = required;
  placeholder.selected = !selectedValue;
  select.appendChild(placeholder);

  variants.forEach((variant) => {
    const option = createOption(variant);
    if (selectedValue && String(variant.id) === String(selectedValue)) {
      option.selected = true;
    }
    select.appendChild(option);
  });

  syncDropdownButtonValue(select, dropdownValue, labels.placeholder);
  select.addEventListener('change', () => syncDropdownButtonValue(select, dropdownValue, labels.placeholder));

  const removeButton = document.createElement('button');
  removeButton.type = 'button';
  removeButton.className = 'button button--tertiary print-options__remove';
  removeButton.dataset.printRemoveRow = '';
  removeButton.textContent = labels.remove;

  dropdown.appendChild(dropdownButton);
  dropdown.appendChild(select);
  selectField.appendChild(selectLabel);
  selectField.appendChild(dropdown);
  row.appendChild(selectField);
  row.appendChild(removeButton);

  return row;
}

function isPrintRequired(root) {
  return root?.dataset?.printRequired === 'true';
}

function updateRowControls(root) {
  const rows = root.querySelectorAll(PRINT_ROW_SELECTOR);
  rows.forEach((row, index) => {
    const removeButton = row.querySelector('[data-print-remove-row]');
    if (!removeButton) return;

    const disableRemove = isPrintRequired(root) && rows.length === 1;
    removeButton.disabled = disableRemove;
    removeButton.setAttribute('aria-disabled', disableRemove ? 'true' : 'false');
    row.dataset.printRowIndex = String(index);
  });
}

function hasValidRows(root) {
  if (!isPrintRequired(root)) return true;

  const selects = [...root.querySelectorAll(PRINT_SELECT_SELECTOR)];
  return selects.length > 0 && selects.every((select) => select.value);
}

function validateRows(root) {
  const error = root.querySelector('[data-print-options-error]');
  const isValid = hasValidRows(root);

  if (!isValid) {
    if (error) {
      error.hidden = false;
      error.textContent = root.printLabels?.error ?? DEFAULT_LABELS.error;
    }
    return false;
  }

  if (error) {
    error.hidden = true;
    error.textContent = '';
  }

  return true;
}

function updateAddToCartButtonState(form, root) {
  const addToCartButton = form.querySelector('button[ref="addToCartButton"]');
  if (!(addToCartButton instanceof HTMLButtonElement)) return;
  if (addToCartButton.dataset.variantSelectionPending === 'true') return;
  const canAddToCart = addToCartButton.dataset.canAddToCart === 'true';

  if (!isPrintRequired(root)) {
    delete addToCartButton.dataset.printRequiredDisabled;
    if (canAddToCart) {
      addToCartButton.disabled = false;
    }
    return;
  }

  const isValid = hasValidRows(root);
  if (!isValid) {
    if (canAddToCart) {
      addToCartButton.disabled = true;
      addToCartButton.dataset.printRequiredDisabled = 'true';
    }
    return;
  }

  if (addToCartButton.dataset.printRequiredDisabled === 'true') {
    if (canAddToCart) {
      addToCartButton.disabled = false;
    }
    delete addToCartButton.dataset.printRequiredDisabled;
  }
}

function clearPrintRequirementState(form) {
  const addToCartButton = form.querySelector('button[ref="addToCartButton"]');
  if (!(addToCartButton instanceof HTMLButtonElement)) return;
  if (addToCartButton.dataset.variantSelectionPending === 'true') return;
  const canAddToCart = addToCartButton.dataset.canAddToCart === 'true';

  if (addToCartButton.dataset.printRequiredDisabled === 'true') {
    if (canAddToCart) {
      addToCartButton.disabled = false;
    }
    delete addToCartButton.dataset.printRequiredDisabled;
  }
}

function mountPrintOptions(form, config) {
  if (!form || form.querySelector(PRINT_ROOT_SELECTOR)) return;

  const root = document.createElement('div');
  const printRequired = config.required !== false;
  root.className = printRequired
    ? 'print-options gpo-group gpo-form__group gpo-col-12 color-scheme-2'
    : 'print-options color-scheme-2';
  root.dataset.printOptionsRoot = '';
  root.dataset.printEnabled = 'true';
  root.dataset.printRequired = printRequired ? 'true' : 'false';
  root.dataset.printProductId = String(config.productId);
  root.printVariants = Array.isArray(config.variants) ? config.variants : [];
  root.printRequired = printRequired;
  root.printLabels = resolveLabels(config.labels);
  const labels = root.printLabels;

  const title = document.createElement('p');
  title.className = 'print-options__title';
  title.textContent = labels.title;

  const description = document.createElement('p');
  description.className = 'print-options__description';
  description.textContent = root.printRequired ? labels.requiredDescription : labels.optionalDescription;

  const rows = document.createElement('div');
  rows.className = 'print-options__rows';
  rows.dataset.printOptionsRows = '';

  if (root.printRequired) {
    rows.appendChild(createPrintRow(config.variants, labels, '', true));
  }

  const actions = document.createElement('div');
  actions.className = 'print-options__actions';

  const addButton = document.createElement('button');
  addButton.type = 'button';
  addButton.className = 'button button--secondary print-options__add';
  addButton.dataset.printAddRow = '';
  addButton.textContent = labels.add;

  const error = document.createElement('p');
  error.className = 'print-options__error';
  error.hidden = true;
  error.setAttribute('role', 'alert');
  error.dataset.printOptionsError = '';

  actions.appendChild(addButton);

  root.appendChild(title);
  root.appendChild(description);
  root.appendChild(rows);
  root.appendChild(actions);
  root.appendChild(error);

  const productButtons = form.querySelector('.product-form-buttons');
  if (productButtons && productButtons.parentNode) {
    productButtons.parentNode.insertBefore(root, productButtons);
  } else {
    const addToCart = form.querySelector('add-to-cart-component');
    if (addToCart && addToCart.parentNode) {
      addToCart.parentNode.insertBefore(root, addToCart);
    } else {
      form.appendChild(root);
    }
  }

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.closest('[data-print-add-row]')) {
      rows.appendChild(createPrintRow(config.variants, labels, '', root.printRequired));
      updateRowControls(root);
      updateAddToCartButtonState(form, root);
      return;
    }

    const removeButton = target.closest('[data-print-remove-row]');
    if (!removeButton) return;

    const row = removeButton.closest(PRINT_ROW_SELECTOR);
    if (!row) return;

    const currentRows = rows.querySelectorAll(PRINT_ROW_SELECTOR);
    if (root.printRequired && currentRows.length <= 1) return;

    row.remove();
    updateRowControls(root);
    validateRows(root);
    updateAddToCartButtonState(form, root);
  });

  root.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.matches(PRINT_SELECT_SELECTOR)) return;
    validateRows(root);
    updateAddToCartButtonState(form, root);
  });

  root.validatePrintOptions = () => validateRows(root);
  updateRowControls(root);
  updateAddToCartButtonState(form, root);

  const addToCartObserver = new MutationObserver(() => {
    updateAddToCartButtonState(form, root);
  });

  addToCartObserver.observe(form, {
    childList: true,
    subtree: true,
  });
}

function parseConfig(script) {
  try {
    const parsed = JSON.parse(script.textContent || '{}');
    if (!parsed?.enabled || !Array.isArray(parsed.variants) || parsed.variants.length === 0) return null;

    return parsed;
  } catch (error) {
    console.error('Unable to parse print options config', error);
    return null;
  }
}

function initializePrintOptions() {
  const productForms = [...document.querySelectorAll('form[data-type="add-to-cart-form"]')];

  productForms.forEach((form) => {
    const existingRoots = [...form.querySelectorAll(PRINT_ROOT_SELECTOR)];
    existingRoots.forEach((root) => root.remove());
    clearPrintRequirementState(form);
  });

  const configNodes = [...document.querySelectorAll('[data-print-options-config]')];
  configNodes.forEach((node) => {
    if (!(node instanceof HTMLScriptElement)) return;

    const config = parseConfig(node);
    if (!config) return;

    const productFormComponents = document.querySelectorAll(
      `product-form-component[data-product-id="${config.productId}"]`
    );

    productFormComponents.forEach((component) => {
      const form = component.querySelector('form[data-type="add-to-cart-form"]');
      if (!(form instanceof HTMLFormElement)) return;

      mountPrintOptions(form, config);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializePrintOptions);
} else {
  initializePrintOptions();
}
